import { useAppSelector } from '@/hooks/hooks';
import type {
  BillingTier,
  OrgAccessState,
  SubscriptionStatus,
} from '@/types/billing';

/**
 * WEB-3302 юнит «тариф» — одно правило вида карточки тарифа организации.
 *
 * Экран «Баланс/Тариф» решает, что рисовать (карточка тарифа, плашки «Тариф
 * не оплачен» и «Оплата не прошла», карточка «Начните пробный период»), только
 * по значению `planView`. Состояние доступа организации — с сервера
 * (`accessState` в `GET /billing`, `resolveOrgAccessState` в
 * `backend/src/shared/constants/billing-tiers.ts`); кабинет его не пересчитывает.
 * `tier` читается одним местом — строкой `hidden` (сервер скрыл тариф от
 * читателя без доступа к деньгам, WEB-1290).
 *
 * Строки таблицы (сверху вниз, первая подходящая):
 *  - тариф скрыт (`UNKNOWN`) → `hidden`;
 *  - `accessState` нет (`null` / поля нет — сервер старше кабинета) → `unknown`;
 *  - иначе — строка `ACCESS_STATE_VIEW` по значению; значения вне перечня
 *    (сервер новее кабинета) не угадываются → `unknown`.
 *
 * Множество ключей `ACCESS_STATE_VIEW` равно перечню `OrgAccessState` контракта
 * в обе стороны: на сборке — тип `Record`, в тестах — сторож `planView.test.ts`
 * (читает `api/generated/schema.ts`).
 */
export type PlanView =
  | 'hidden'
  | 'active'
  | 'trial'
  | 'none'
  | 'unpaid'
  | 'pastdue'
  | 'debt'
  | 'unknown';

export const ACCESS_STATE_VIEW: Readonly<
  Record<OrgAccessState, (status: SubscriptionStatus | null) => PlanView>
> = {
  paid: (status) => (status === 'PAST_DUE' ? 'pastdue' : 'active'),
  trialing: () => 'trial',
  trial_not_started: () => 'none',
  // в эталоне отдельного «пробный закончился» нет (владелец 28.10 «по эталону»)
  trial_ended: () => 'unpaid',
  unpaid: () => 'unpaid',
  // долг по объёму: статус, плашка долга и «Оплатить долг» (WEB-3302 юнит
  // «условия», CurrentPlanCard.tsx `UsageDebtNotice`)
  unpaid_usage_debt: () => 'debt',
};

/** Значения состояния доступа — ключи таблицы правила (одна дверь для разбора ответа). */
export const ORG_ACCESS_STATES = Object.keys(ACCESS_STATE_VIEW) as [
  OrgAccessState,
  ...OrgAccessState[],
];

export interface PlanViewInput {
  readonly tier: BillingTier;
  readonly accessState: OrgAccessState | null | undefined;
  readonly subscriptionStatus: SubscriptionStatus | null | undefined;
}

export function planView({
  tier,
  accessState,
  subscriptionStatus,
}: PlanViewInput): PlanView {
  if (tier === 'UNKNOWN') return 'hidden';
  if (accessState === null || accessState === undefined) return 'unknown';
  if (!Object.prototype.hasOwnProperty.call(ACCESS_STATE_VIEW, accessState))
    return 'unknown';
  return ACCESS_STATE_VIEW[accessState](subscriptionStatus ?? null);
}

/**
 * Вид тарифа из среза — одна дверь для карточки тарифа (`CurrentPlanCard.tsx`)
 * и «Пополнить» (`TopUpPackages.tsx`): своего разбора `accessState` у них нет
 * (WEB-3302 юнит «условия» — хук перенесён сюда из `CurrentPlanCard.tsx`).
 */
export function usePlanView(): PlanView {
  const tier = useAppSelector((state) => state.billing?.tier ?? 'UNKNOWN');
  const accessState = useAppSelector(
    (state) => state.billing?.accessState ?? null,
  );
  const status = useAppSelector(
    (state) => state.billing?.subscription?.status ?? null,
  );
  return planView({ tier, accessState, subscriptionStatus: status });
}
